import { useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/cn';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastProps {
  title?: string;
  message?: string;
  description?: string;
  type?: ToastType;
  duration?: number;
  onClose: () => void;
  action?: ToastAction;
  thumbnailUrl?: string | null;
  onClick?: () => void;
}

export function Toast({
  title,
  message,
  description,
  type = 'info',
  duration = 5000,
  onClose,
  action,
  thumbnailUrl,
  onClick,
}: ToastProps) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Wait for fade out animation
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const icons = {
    success: CheckCircle,
    error: AlertCircle,
    info: Info,
    warning: AlertTriangle,
  };

  const colors = {
    success: 'bg-white border-olive/40 text-charcoal',
    error: 'bg-white border-terracotta/40 text-charcoal',
    info: 'bg-white border-stone/50 text-charcoal',
    warning: 'bg-white border-amber-300 text-charcoal',
  };

  const iconColors = {
    success: 'text-olive',
    error: 'text-terracotta',
    info: 'text-sand',
    warning: 'text-amber-600',
  };

  const Icon = icons[type];
  const colorClass = colors[type];
  const iconColorClass = iconColors[type];

  return (
    <div
      className={cn(
        'transition-all duration-200',
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
      )}
    >
      <div
        className={cn(
          colorClass,
          'border rounded-2xl shadow-lg shadow-charcoal/15 p-4 min-w-[300px] max-w-md flex items-start gap-3'
        )}
      >
        <button
          type="button"
          onClick={onClick}
          disabled={!onClick}
          aria-disabled={!onClick}
          className={cn('flex-1 flex items-start gap-3 text-left', !onClick && 'cursor-default')}
        >
          <Icon className={cn('size-5', iconColorClass, 'flex-shrink-0 mt-0.5')} />
          {thumbnailUrl && (
            <div className="size-12 rounded-xl overflow-hidden border border-stone bg-white flex-shrink-0">
              <img src={thumbnailUrl} alt="" className="size-full object-cover" />
            </div>
          )}
          <div className="min-w-0">
            {title && (
              <p className="text-sm font-medium text-balance">{title}</p>
            )}
            {message && (
              <p className="text-sm text-charcoal text-pretty">{message}</p>
            )}
            {description && (
              <p className="text-xs text-sand mt-1 text-pretty">{description}</p>
            )}
          </div>
        </button>
        <div className="flex flex-col items-end gap-2">
          {action && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
              }}
              className="text-xs font-medium text-olive underline underline-offset-4 hover:opacity-80 transition"
            >
              {action.label}
            </button>
          )}
          <button
            onClick={handleClose}
            className="text-sand hover:text-charcoal transition flex-shrink-0"
            aria-label="Dismiss notification"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

interface ToastContainerProps {
  toasts: Array<{
    id: string;
    title?: string;
    message?: string;
    description?: string;
    type?: ToastType;
    action?: ToastAction;
    thumbnailUrl?: string | null;
    onClick?: () => void;
  }>;
  onRemove: (id: string) => void;
}

export function ToastContainer({ toasts, onRemove }: ToastContainerProps) {
  return (
    <>
      {toasts.map((toast, index) => (
        <div
          key={toast.id}
          style={{
            bottom: `calc(env(safe-area-inset-bottom) + ${4 + index * 88}px)`,
          }}
          className="fixed right-4 z-50"
        >
          <Toast
            title={toast.title}
            message={toast.message}
            description={toast.description}
            type={toast.type}
            action={toast.action}
            thumbnailUrl={toast.thumbnailUrl}
            onClick={toast.onClick}
            onClose={() => onRemove(toast.id)}
          />
        </div>
      ))}
    </>
  );
}
